import { calibrateFromResponse } from '../clock';
import type { ApiOrder, StoreSettings } from '../types';

export interface FetchOrdersInput {
  settings: StoreSettings;
  xApiKey: string;
  createdAfter: string;
  signal?: AbortSignal;
}

export interface FetchSettingsInput {
  storeId: number;
  signal?: AbortSignal;
}

/**
 * Reads the store settings via the same-origin server route, then fetches
 * orders via the proxy at `/order-api/orders`. Neither call ever
 * exposes the Order Gateway origin or the Settings API origin to the
 * browser's network tab.
 */
export async function fetchSettings(input: FetchSettingsInput): Promise<StoreSettings> {
  const url = new URL('/api/settings', window.location.origin);
  url.searchParams.set('storeId', String(input.storeId));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { accept: 'application/json' },
    signal: input.signal,
  });
  if (!res.ok) {
    throw new Error(`settings_request_failed: ${res.status}`);
  }
  return (await res.json()) as StoreSettings;
}

const PAGE_SIZE = 200;
const MAX_PAGES = 25;

export async function fetchOrders(input: FetchOrdersInput): Promise<ApiOrder[]> {
  const byId = new Map<string, ApiOrder>();
  let offset = 0;
  let total: number | null = null;

  for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex++) {
    const url = new URL('/api/orders', window.location.origin);
    url.searchParams.set('storeId', String(input.settings.storeId));
    url.searchParams.set('limit', String(PAGE_SIZE));
    url.searchParams.set('offset', String(offset));
    url.searchParams.set('createdAfter', input.createdAfter);

    const requestStartedAt = Date.now();
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        authorization: `ApiKey ${input.xApiKey}`,
        accept: 'application/json',
        'x-order-api-url': input.settings.httpUrl,
      },
      signal: input.signal,
    });
    if (!res.ok) {
      throw new Error(`orders_request_failed: ${res.status}`);
    }
    const body = (await res.json()) as { orders?: ApiOrder[]; total?: number };
    // A parsed JSON body means this really is our proxy answering (not a
    // captive portal or some intermediary), so its clock is safe to learn
    // from. See src/clock.ts for why the TV clock is not trusted.
    calibrateFromResponse(res, requestStartedAt);
    const rows = body.orders ?? [];
    if (typeof body.total === 'number' && Number.isFinite(body.total)) total = body.total;
    for (const row of rows) {
      if (row && row.id) byId.set(row.id, row);
    }

    offset += rows.length;
    if (rows.length === 0) break;
    if (total !== null && offset >= total) break;
  }

  const orders: ApiOrder[] = [];
  byId.forEach((order) => orders.push(order));
  return orders;
}
